import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Card, Spinner } from '../components/ui';

/**
 * Grafiki longów: pick a rendered long-form and inspect every image that was
 * used — with the prompt, provider and model that produced it. Older renders
 * stored only the image, so for them we show the provider/prompt version from
 * the asset record and say the prompt was not saved.
 */
export function FboLongAssetsPanel({ onError }: { onError: (error: unknown) => void }) {
  const [videos, setVideos] = useState<Awaited<ReturnType<typeof api.fboFinance.longVideos>>['videos']>([]);
  const [selected, setSelected] = useState('');
  const [detail, setDetail] = useState<Awaited<ReturnType<typeof api.fboFinance.longVideoAssets>> | null>(null);
  const [loading, setLoading] = useState(false);

  const loadList = useCallback(async () => {
    try {
      const result = await api.fboFinance.longVideos(200);
      setVideos(result.videos);
      setSelected((current) => current || result.videos[0]?.videoJobId || '');
    } catch (cause) { onError(cause); }
  }, [onError]);

  useEffect(() => { void loadList(); }, [loadList]);

  useEffect(() => {
    if (!selected) { setDetail(null); return; }
    let cancelled = false;
    setLoading(true);
    api.fboFinance.longVideoAssets(selected)
      .then((result) => { if (!cancelled) setDetail(result); })
      .catch((cause) => { if (!cancelled) onError(cause); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [selected, onError]);

  const fmtDuration = (seconds: number | null) => (seconds == null ? '—' : `${Math.floor(seconds / 60)}:${String(Math.round(seconds % 60)).padStart(2, '0')}`);

  return <Card title="Grafiki longów">
    <p className="muted">Wybierz wyrenderowany long-form, żeby zobaczyć wszystkie użyte grafiki, prompt i model graficzny.</p>
    <label className="field" style={{ maxWidth: 620 }}>
      <span>Long-form ({videos.length})</span>
      <select className="input" value={selected} disabled={loading || !videos.length} onChange={(event) => setSelected(event.target.value)}>
        {!videos.length && <option value="">Brak wyrenderowanych long-formów</option>}
        {videos.map((video) => <option key={video.videoJobId} value={video.videoJobId}>{video.title} · {video.scenes} grafik · {fmtDuration(video.durationSeconds)}</option>)}
      </select>
    </label>
    {loading && <Spinner />}
    {detail && !loading && <>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', margin: '12px 0' }}>
        {detail.thumbnailUrl && <div style={{ border: '1px solid var(--border,#333)', borderRadius: 8, padding: 8 }}>
          <img src={detail.thumbnailUrl} alt="Miniatura long-formu" style={{ width: 240, borderRadius: 6, display: 'block' }} />
          <div className="muted" style={{ marginTop: 6 }}>Miniatura</div>
        </div>}
        {detail.presenter?.baseUrl && <div style={{ border: '1px solid var(--border,#333)', borderRadius: 8, padding: 8 }}>
          <img src={detail.presenter.baseUrl} alt="Baza prezentera" style={{ width: 160, borderRadius: 6, display: 'block' }} />
          <div className="muted" style={{ marginTop: 6 }}>Prezenter (baza)</div>
        </div>}
        {Object.entries(detail.presenter?.poses ?? {}).slice(0, 4).map(([pose, url]) => <div key={pose} style={{ border: '1px solid var(--border,#333)', borderRadius: 8, padding: 8 }}>
          <img src={String(url)} alt={`Poza ${pose}`} style={{ width: 90, borderRadius: 6, display: 'block' }} />
          <div className="muted" style={{ marginTop: 6 }}>Poza: {pose}</div>
        </div>)}
      </div>
      <div className="table-wrap">
        <table className="table">
          <thead><tr><th>#</th><th>Grafika</th><th>Scena</th><th>Model</th><th>Prompt</th></tr></thead>
          <tbody>
            {detail.scenes.map((scene) => <tr key={`${scene.index}-${scene.assetId ?? 'none'}`}>
              <td>{scene.index}</td>
              <td>{scene.imageUrl ? <a href={scene.imageUrl} target="_blank" rel="noreferrer"><img src={scene.imageUrl} alt={`Grafika ${scene.index}`} style={{ width: 140, borderRadius: 6, display: 'block' }} /></a> : <span className="muted">brak</span>}</td>
              <td style={{ maxWidth: 260 }}>{scene.headline && <div><strong>{scene.headline}</strong></div>}<span className="muted">{scene.text}</span></td>
              <td style={{ whiteSpace: 'nowrap' }}>{scene.provider ?? '—'}<br /><span className="muted">{scene.model ?? 'model nie zapisany'}</span></td>
              <td style={{ maxWidth: 420 }}>
                {scene.prompt
                  ? <details><summary style={{ cursor: 'pointer' }}>Pokaż prompt (zapisany)</summary><pre style={{ whiteSpace: 'pre-wrap', fontSize: 12, marginTop: 6 }}>{scene.prompt}</pre></details>
                  : <details><summary style={{ cursor: 'pointer' }}>Pokaż wzorcowy prompt</summary><div className="muted" style={{ marginTop: 4 }}>Ten render nie zapisał promptu{scene.promptVersion ? ` (wersja: ${scene.promptVersion})` : ''} — poniżej szablon, który dostaje generator dla tej sceny:</div><pre style={{ whiteSpace: 'pre-wrap', fontSize: 12, marginTop: 6 }}>{scene.promptTemplate}</pre></details>}
              </td>
            </tr>)}
          </tbody>
        </table>
      </div>
    </>}
  </Card>;
}
